// /flights — list of upcoming bookings with inline cancel.
//
// Cancel honours the 1h cutoff server-side (RCF-005). If the cutoff has
// passed the API responds 409 with the human-readable message; we surface
// it directly so the user knows they need to ask the admin.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Section, Cell, List, Button, Caption, Spinner, Banner,
} from "@telegram-apps/telegram-ui";
import { api } from "../lib/api.js";
import { usePageBack } from "../lib/telegram.js";

const RU_WEEKDAYS = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const RU_MONTHS = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

const CLASS_LABELS = {
  F3A: "✈️ Самолёт",
  F9U: "🚁 Дрон",
};

export default function Flights() {
  const navigate = useNavigate();
  usePageBack("/");
  const [bookings, setBookings] = useState(null);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [feedback, setFeedback] = useState(null);

  function load() {
    api("/api/v1/me/bookings")
      .then((r) => setBookings(r.bookings || []))
      .catch((e) => setError(`Не удалось загрузить слоты: ${e.message}`));
  }

  useEffect(() => { load(); }, []);

  async function cancelOne(b) {
    if (!confirmCancel(b)) return;
    setCancelling(b.id);
    setFeedback(null);
    try {
      await api(`/api/v1/bookings/${b.id}`, { method: "DELETE" });
      setFeedback({ kind: "ok", text: `Слот #${b.id} отменён.` });
      load();
    } catch (e) {
      setFeedback({ kind: "err", text: e.message || "Не удалось отменить слот." });
    } finally {
      setCancelling(null);
    }
  }

  if (bookings === null && !error) {
    return (
      <List>
        <Section><Cell before={<Spinner size="m" />}>Загружаем слоты…</Cell></Section>
      </List>
    );
  }

  if (error) {
    return (
      <List>
        <Banner type="section" header="Ошибка">{error}</Banner>
      </List>
    );
  }

  if (bookings.length === 0) {
    return (
      <List>
        <Section header="Мои полёты">
          <Cell multiline subtitle="Запишитесь на ближайший открытый слот — это займёт меньше минуты.">
            <span style={{ fontSize: 28 }}>🛬</span> Пока ничего не забронировано
          </Cell>
          <Cell>
            <Button stretched mode="filled" onClick={() => navigate("/book")}>
              📅 Забронировать
            </Button>
          </Cell>
        </Section>
      </List>
    );
  }

  const now = Date.now();
  const upcoming = bookings.filter((b) => new Date(b.end_at).getTime() >= now);
  const past = bookings.filter((b) => new Date(b.end_at).getTime() < now);

  return (
    <List>
      {feedback && (
        <Banner type="section" header={feedback.kind === "ok" ? "Готово" : "Ошибка"}>
          {feedback.text}
        </Banner>
      )}

      {upcoming.length > 0 && (
        <Section header={`Ближайшие · ${upcoming.length}`}>
          {upcoming.map((b) => (
            <BookingRow
              key={b.id}
              booking={b}
              onCancel={() => cancelOne(b)}
              cancelling={cancelling === b.id}
            />
          ))}
        </Section>
      )}

      {past.length > 0 && (
        <Section header={`История · ${past.length}`}>
          {past.slice(0, 10).map((b) => (
            <PastRow key={b.id} booking={b} />
          ))}
        </Section>
      )}

      <Section>
        <Cell>
          <Button stretched mode="plain" onClick={() => navigate("/book")}>
            Забронировать ещё
          </Button>
        </Cell>
        <Cell multiline>
          <Caption level="1">
            Отмена доступна за 1 час до начала. Позже — только через админа.
          </Caption>
        </Cell>
      </Section>
    </List>
  );
}

function BookingRow({ booking, onCancel, cancelling }) {
  const navigate = useNavigate();
  const start = new Date(booking.start_at);
  const end = new Date(booking.end_at);
  const dateText = formatDateRu(start);
  const hhmm = formatHHMM(start);
  const timeText = `${hhmm}–${formatHHMM(end)}`;
  const ymd = `${start.getFullYear()}-${String(start.getMonth()+1).padStart(2,"0")}-${String(start.getDate()).padStart(2,"0")}`;
  const minsToStart = Math.round((start.getTime() - Date.now()) / 60000);
  const tooLate = minsToStart < 60;
  const pill = pillFor(minsToStart);
  const cancelLabel = cancelling
    ? "Отменяем…"
    : tooLate
      ? "🔒 Только через админа"
      : "🗑 Отменить";

  return (
    <div className="rcf-booking">
      <div className="rcf-booking__head">
        <div>
          <div className="rcf-booking__date">{dateText}</div>
          <div className="rcf-booking__time">{timeText}</div>
        </div>
        <span className={`rcf-pill rcf-pill--${pill.tone}`}>{pill.label}</span>
      </div>
      <div className="rcf-booking__meta">
        <span>{booking.airfield_name}</span>
        <span>·</span>
        <span>{CLASS_LABELS[booking.class_code] || booking.class_code}</span>
        <span>·</span>
        <span>#{booking.id}</span>
      </div>
      <div className="rcf-booking__actions">
        <Button
          mode="plain"
          size="s"
          onClick={() => navigate(`/slot/${booking.airfield_slug}/${ymd}/${hhmm}`)}
        >
          Подробнее
        </Button>
        <Button
          mode="plain"
          size="s"
          disabled={tooLate || cancelling}
          onClick={onCancel}
        >
          {cancelLabel}
        </Button>
      </div>
    </div>
  );
}

function PastRow({ booking }) {
  const start = new Date(booking.start_at);
  const end = new Date(booking.end_at);
  return (
    <Cell
      subtitle={`${booking.airfield_name} · ${CLASS_LABELS[booking.class_code] || booking.class_code}`}
      multiline
      after={<span className="rcf-pill rcf-pill--neutral">завершён</span>}
    >
      {formatDateRu(start)} · {formatHHMM(start)}–{formatHHMM(end)}
    </Cell>
  );
}

function pillFor(minsToStart) {
  if (minsToStart < 0) return { tone: "neutral", label: "идёт / завершён" };
  if (minsToStart < 60) return { tone: "warn", label: "скоро" };
  if (minsToStart < 60 * 24) return { tone: "info", label: "сегодня" };
  if (minsToStart < 60 * 24 * 2) return { tone: "info", label: "завтра" };
  return { tone: "ok", label: "запланирован" };
}

function confirmCancel(b) {
  const time = `${formatHHMM(new Date(b.start_at))}–${formatHHMM(new Date(b.end_at))}`;
  return window.confirm(
    `Отменить слот #${b.id} на ${formatDateRu(new Date(b.start_at))} ${time}?`
  );
}

function formatDateRu(d) {
  return `${RU_WEEKDAYS[d.getDay()]} ${d.getDate()} ${RU_MONTHS[d.getMonth()]}`;
}

function formatHHMM(d) {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
